// Чистая логика экранов плана: недели и месяцы, подписи статусов и кардио, план и факт, нагрузка.

import type { BadgeTone } from '@/components/ui'
import { formatNumber, parseDecimal } from '@/lib/format'
import { addDays, parseIsoDate, toIsoDate, todayIso } from '@/lib/periods'

import type { Cardio, CardioSport, MuscleVolume, Planned, PlanKind, PlanStatus } from './api'

export const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const
export const WEEKDAYS_FULL = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'] as const

/** 0 = Пн … 6 = Вс. */
export function weekdayOf(iso: string): number {
  return (parseIsoDate(iso).getDay() + 6) % 7
}

export function mondayOf(iso: string): string {
  return addDays(iso, -weekdayOf(iso))
}

export function weekDays(iso: string): string[] {
  const monday = mondayOf(iso)
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

export type GridDay = { day: string; inMonth: boolean }

/** Полные недели (с понедельника), покрывающие месяц дня `iso`. */
export function monthGrid(iso: string): GridDay[][] {
  const date = parseIsoDate(iso)
  const month = date.getMonth()
  const first = toIsoDate(new Date(date.getFullYear(), month, 1))
  const last = toIsoDate(new Date(date.getFullYear(), month + 1, 0))
  const weeks: GridDay[][] = []
  let cursor = mondayOf(first)
  while (cursor <= last) {
    weeks.push(
      weekDays(cursor).map((day) => ({ day, inMonth: parseIsoDate(day).getMonth() === month })),
    )
    cursor = addDays(cursor, 7)
  }
  return weeks
}

export function monthBounds(iso: string): { start: string; end: string } {
  const grid = monthGrid(iso)
  return { start: grid[0]![0]!.day, end: grid[grid.length - 1]![6]!.day }
}

export function shiftMonth(iso: string, delta: number): string {
  const d = parseIsoDate(iso)
  return toIsoDate(new Date(d.getFullYear(), d.getMonth() + delta, 1))
}

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
] as const

export function monthTitle(iso: string): string {
  const d = parseIsoDate(iso)
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export const STATUS_META: Record<PlanStatus, { label: string; tone: BadgeTone }> = {
  planned: { label: 'Впереди', tone: 'neutral' },
  done: { label: 'Выполнено', tone: 'accent' },
  skipped: { label: 'Пропущено', tone: 'warning' },
  missed: { label: 'Не проведено', tone: 'danger' },
}

/** Цвет точки статуса в сетке месяца. */
export const STATUS_DOT: Record<PlanStatus, string> = {
  planned: 'bg-muted',
  done: 'bg-accent',
  skipped: 'bg-warning',
  missed: 'bg-danger',
}

export const SPORTS: { value: CardioSport; label: string }[] = [
  { value: 'run', label: 'Бег' },
  { value: 'bike', label: 'Велосипед' },
  { value: 'walk', label: 'Ходьба' },
  { value: 'swim', label: 'Плавание' },
  { value: 'other', label: 'Другое кардио' },
]

const SPORT_SHORT: Record<CardioSport, string> = {
  run: 'Бег',
  bike: 'Велосипед',
  walk: 'Ходьба',
  swim: 'Плавание',
  other: 'Кардио',
}

export function cardioLabel(cardio: Cardio): string {
  const parts: string[] = [SPORT_SHORT[cardio.sport] ?? 'Кардио']
  if (cardio.duration_s) parts.push(`${Math.round(cardio.duration_s / 60)} мин`)
  if (cardio.distance_m) parts.push(`${formatNumber(cardio.distance_m / 1000, { digits: 2 })} км`)
  if (cardio.hr_zone) parts.push(`зона ${cardio.hr_zone}`)
  return parts.join(' · ')
}

export function plannedTitle(p: Pick<Planned, 'kind' | 'template_name' | 'cardio'>): string {
  if (p.kind === 'cardio') return p.cardio ? cardioLabel(p.cardio) : 'Кардио'
  return p.template_name ?? 'Силовая'
}

export type CardioForm = { sport: CardioSport; mode: 'time' | 'distance'; value: string; zone: string }

export const EMPTY_CARDIO_FORM: CardioForm = { sport: 'run', mode: 'time', value: '', zone: '' }

export type GarminState = 'none' | 'sent' | 'stale'

/** Отправка на часы: не отправлено, отправлено (актуально), на часах старая версия. */
export function garminState(p: Pick<Planned, 'garmin_workout_id' | 'garmin_stale'>): GarminState {
  if (p.garmin_workout_id === null) return 'none'
  return p.garmin_stale ? 'stale' : 'sent'
}

/** На часы отправляется только предстоящая плановая с шаблоном или кардио. */
export function canPushToWatch(
  p: Pick<Planned, 'status' | 'day' | 'kind' | 'template_id' | 'cardio'>,
  today: string = todayIso(),
): boolean {
  if (p.status !== 'planned' || p.day < today) return false
  return p.kind === 'strength' ? p.template_id !== null : p.cardio !== null
}

export function cardioToForm(cardio: Cardio | null): CardioForm {
  if (!cardio) return EMPTY_CARDIO_FORM
  const byDistance = !cardio.duration_s && Boolean(cardio.distance_m)
  return {
    sport: cardio.sport,
    mode: byDistance ? 'distance' : 'time',
    value: byDistance
      ? formatNumber((cardio.distance_m ?? 0) / 1000, { digits: 2 })
      : String(Math.round((cardio.duration_s ?? 0) / 60)),
    zone: cardio.hr_zone ? String(cardio.hr_zone) : '',
  }
}

export function cardioFromForm(form: CardioForm): { ok: true; cardio: Cardio } | { ok: false; error: string } {
  const value = parseDecimal(form.value)
  if (value === null || value <= 0) {
    return { ok: false, error: form.mode === 'time' ? 'Укажи длительность в минутах' : 'Укажи дистанцию в км' }
  }
  const zone = form.zone ? Number(form.zone) : null
  return {
    ok: true,
    cardio: {
      sport: form.sport,
      duration_s: form.mode === 'time' ? Math.round(value * 60) : null,
      distance_m: form.mode === 'distance' ? Math.round(value * 1000) : null,
      hr_zone: zone,
    },
  }
}

export type PlanItemDraft = { kind: PlanKind; template_id: string; cardio: CardioForm }

export function draftFrom(kind: PlanKind, templateId: string | null, cardio: Cardio | null): PlanItemDraft {
  return { kind, template_id: templateId ?? '', cardio: cardio ? cardioToForm(cardio) : EMPTY_CARDIO_FORM }
}

/** Проверка черновика: силовая — шаблон, кардио — объём. */
export function draftToItem(
  draft: PlanItemDraft,
): { ok: true; template_id: string | null; cardio: Cardio | null } | { ok: false; error: string } {
  if (draft.kind === 'strength') {
    if (!draft.template_id) return { ok: false, error: 'Выбери шаблон' }
    return { ok: true, template_id: draft.template_id, cardio: null }
  }
  const parsed = cardioFromForm(draft.cardio)
  if (!parsed.ok) return parsed
  return { ok: true, template_id: null, cardio: parsed.cardio }
}

export function groupByDay(items: Planned[]): Record<string, Planned[]> {
  const groups: Record<string, Planned[]> = {}
  for (const item of items) (groups[item.day] ??= []).push(item)
  return groups
}

/** Тренировки, не закрывшие ни одну плановую, — «без плана» рядом с планом. */
export function unplannedSessions<T extends { id: string; started_at: string }>(sessions: T[], planned: Planned[]): T[] {
  const linked = new Set(planned.map((p) => p.workout_session_id).filter(Boolean))
  return sessions.filter((s) => !linked.has(s.id))
}

export function sessionDay(startedAt: string): string {
  return toIsoDate(new Date(startedAt))
}

export type VolumeRow = {
  muscle: string
  label: string
  sets: number
  average: number
  delta: number
  tonnage: number
}

/** Строки «Нагрузки»: группы недели и группы, что были в прошлые недели (с нулём сейчас). */
export function volumeRows(
  current: MuscleVolume[],
  averageSets: Record<string, number>,
  labelOf: (key: string) => string,
): VolumeRow[] {
  const rows: VolumeRow[] = current.map((m) => {
    const average = averageSets[m.muscle] ?? 0
    return {
      muscle: m.muscle,
      label: m.label_ru,
      sets: m.sets,
      average,
      delta: round1(m.sets - average),
      tonnage: m.tonnage_kg,
    }
  })
  const seen = new Set(rows.map((r) => r.muscle))
  for (const [muscle, average] of Object.entries(averageSets)) {
    if (seen.has(muscle)) continue
    rows.push({ muscle, label: labelOf(muscle), sets: 0, average, delta: round1(-average), tonnage: 0 })
  }
  return rows.sort((a, b) => b.sets - a.sets || b.average - a.average || a.label.localeCompare(b.label, 'ru'))
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}
